import React, { useEffect, useState } from "react";
import secureAxios from "../utils/secureAxios";
import { formatPhone } from "../utils/formatPhone";

const TenantInfoForm = () => {
  const [formData, setFormData] = useState({
    tenant_name: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    logo_url: "",
    // Invoice settings
    enable_invoices: false,
    invoice_number_prefix: "INV",
    invoice_notes: "",
  });

  const [states, setStates] = useState([]);
  const [message, setMessage] = useState("");
  const [logoUploadMessage, setLogoUploadMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [displayLogoUrl, setDisplayLogoUrl] = useState("");

  // Fixed fetchLogoUrl function with proper parameters
  const fetchLogoUrl = async (rawLogoUrl, tenantId) => {
    if (!rawLogoUrl) {
      console.log("⚠️ No logo URL provided");
      setDisplayLogoUrl("");
      return;
    }

    if (!tenantId) {
      console.log("⚠️ No tenant ID provided");
      setDisplayLogoUrl("");
      return;
    }

    // If it's already a public URL, use it directly
    if (rawLogoUrl.startsWith("http://") || rawLogoUrl.startsWith("https://")) {
      console.log("✅ Public URL detected, using directly:", rawLogoUrl);
      setDisplayLogoUrl(rawLogoUrl);
      return;
    }

    try {
      console.log("🔗 Fetching signed URL for tenant:", tenantId);
      console.log("📁 Raw logo path:", rawLogoUrl);

      // Fetch signed URL from backend
      const response = await secureAxios.get(`/api/tenants/${tenantId}/logo`);

      console.log("✅ Got signed URL response:", response.data);
      setDisplayLogoUrl(response.data.logo_url);
    } catch (error) {
      console.error("❌ Error fetching logo URL:", error);
      setDisplayLogoUrl(""); // Clear logo if can't fetch
    }
  };

  // Fetch current user session and tenant info
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("🔄 Starting tenant info fetch...");
        setLoading(true);

        // Get current user info from server session
        console.log("🔄 Step 1: Fetching user profile...");
        const sessionResponse = await secureAxios.get("/api/auth/profile");
        console.log("✅ Step 1 complete - User profile:", sessionResponse.data);

        if (!sessionResponse.data || !sessionResponse.data.tenant_id) {
          console.error("❌ No user data or tenant_id in profile response");
          setError("Session expired. Please log in again.");
          setLoading(false);
          return;
        }

        const currentUser = sessionResponse.data;
        setUserInfo(currentUser);
        console.log("✅ User info set:", currentUser);

        // Check if user is admin
        if (!currentUser.is_admin) {
          console.error("❌ User is not admin:", currentUser.role);
          setError("You must be an administrator to access this page.");
          setLoading(false);
          return;
        }

        // Fetch tenant information
        console.log(
          "🔄 Step 2: Fetching tenant info for ID:",
          currentUser.tenant_id
        );
        const tenantResponse = await secureAxios.get(
          `/api/tenants/${currentUser.tenant_id}`
        );
        console.log("✅ Step 2 complete - Tenant data:", tenantResponse.data);

        const tenantData = {
          tenant_name: tenantResponse.data.tenant_name || "",
          phone_number: formatPhone(tenantResponse.data.phone_number || ""),
          address_line_1: tenantResponse.data.address_line_1 || "",
          address_line_2: tenantResponse.data.address_line_2 || "",
          city: tenantResponse.data.city || "",
          state_id: tenantResponse.data.state_id || "",
          postal_code: tenantResponse.data.postal_code || "",
          logo_url: tenantResponse.data.logo_url || "",
          // Invoice settings
          enable_invoices: tenantResponse.data.enable_invoices || false,
          invoice_number_prefix:
            tenantResponse.data.invoice_number_prefix || "INV",
          invoice_notes: tenantResponse.data.invoice_notes || "",
        };

        console.log("✅ Tenant data processed:", tenantData);
        setFormData(tenantData);

        // Fetch logo if exists
        if (tenantData.logo_url) {
          console.log("🔄 Step 3: Fetching logo URL...");
          await fetchLogoUrl(tenantData.logo_url, currentUser.tenant_id);
          console.log("✅ Step 3 complete - Logo fetched");
        } else {
          console.log("ℹ️ No logo URL found, skipping logo fetch");
        }

        setError(null);
        console.log("✅ All steps complete - Setting loading to false");
      } catch (err) {
        console.error("❌ Error in fetchData:");
        console.error("❌ Error message:", err.message);
        console.error("❌ Error status:", err.response?.status);
        console.error("❌ Error data:", err.response?.data);
        console.error("❌ Full error:", err);
        setError("Error loading tenant information.");
      } finally {
        console.log("🔄 Setting loading to false");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch states
  useEffect(() => {
    secureAxios
      .get("/api/states")
      .then((res) => setStates(res.data))
      .catch((err) => console.error("Error loading states", err));
  }, []);

  // Clear messages after timeout
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (logoUploadMessage) {
      const timer = setTimeout(() => setLogoUploadMessage(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [logoUploadMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userInfo?.tenant_id) {
      setMessage("❌ Session error. Please refresh the page.");
      return;
    }

    try {
      // Clean phone number before sending
      const payload = {
        ...formData,
        phone_number: formData.phone_number.replace(/\D/g, ""),
        state_id: formData.state_id || null,
        // Invoice settings
        enable_invoices: formData.enable_invoices,
        invoice_number_prefix:
          formData.invoice_number_prefix?.toUpperCase() || "INV",
        invoice_notes: formData.invoice_notes || "",
      };

      await secureAxios.put(`/api/tenants/${userInfo.tenant_id}`, payload);

      setMessage("✅ Tenant information updated successfully.");
    } catch (err) {
      console.error("❌ Failed to update tenant:", err);
      setMessage("❌ Failed to update tenant information.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "phone_number") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      const formatted = formatPhone(digits);
      setFormData((prev) => ({ ...prev, phone_number: formatted }));
    } else if (name === "invoice_number_prefix") {
      // Only allow alphanumeric characters, max 10 chars
      const cleaned = value
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 10)
        .toUpperCase();
      setFormData((prev) => ({ ...prev, invoice_number_prefix: cleaned }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setLogoUploadMessage(
        "❌ Please upload a valid image file (JPEG, PNG, GIF, or WebP)."
      );
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setLogoUploadMessage("❌ File size must be less than 5MB.");
      return;
    }

    // 🔥 FIXED: Check userInfo exists before upload
    if (!userInfo?.tenant_id) {
      setLogoUploadMessage("❌ Session error. Please refresh the page.");
      return;
    }

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append("logo", file);

    try {
      const res = await secureAxios.post(
        `/api/tenants/${userInfo.tenant_id}/logo`,
        formDataUpload
      );

      setFormData((prev) => ({
        ...prev,
        logo_url: res.data.logo_url,
      }));

      await fetchLogoUrl(res.data.logo_url, userInfo.tenant_id);

      setLogoUploadMessage("✅ Logo uploaded successfully.");
    } catch (err) {
      console.error("❌ Logo upload failed:", err);
      setLogoUploadMessage("❌ Logo upload failed. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Generate preview of invoice number format
  const getInvoiceNumberPreview = () => {
    const prefix = formData.invoice_number_prefix || "INV";
    const now = new Date();
    const yearMonth = `${now.getFullYear()}${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`;
    return `${prefix}-${yearMonth}-001`;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="container mt-4">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title mb-3">🏢 Tenant Information</h4>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading tenant information...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container mt-4">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title mb-3">🏢 Tenant Information</h4>
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-body">
          <h4 className="card-title mb-4">🏢 Tenant Information</h4>

          {/* Current Logo Display */}
          {displayLogoUrl && (
            <div className="mb-4 text-center">
              <div className="border rounded p-3 d-inline-block">
                <img
                  src={displayLogoUrl}
                  alt="Tenant Logo"
                  style={{
                    maxHeight: "120px",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    console.error("❌ Logo failed to load:", e);
                    e.target.style.display = "none"; // Hide broken image
                  }}
                />
              </div>
              <div className="text-muted mt-2">Current Company Logo</div>
            </div>
          )}

          {/* Messages */}
          {logoUploadMessage && (
            <div
              className={`alert alert-dismissible fade show ${
                logoUploadMessage.includes("✅")
                  ? "alert-success"
                  : "alert-danger"
              }`}
            >
              {logoUploadMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setLogoUploadMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}

          {message && (
            <div
              className={`alert alert-dismissible fade show ${
                message.includes("✅") ? "alert-success" : "alert-danger"
              }`}
            >
              {message}
              <button
                type="button"
                className="btn-close"
                onClick={() => setMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Company Name */}
            <div className="mb-3">
              <label className="form-label">Company Name</label>
              <input
                className="form-control"
                name="tenant_name"
                value={formData.tenant_name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Logo Upload */}
            <div className="mb-3">
              <label className="form-label">Company Logo</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
              />
              {uploadingLogo && (
                <div className="form-text">
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Uploading...</span>
                  </span>
                  Uploading logo...
                </div>
              )}
              <div className="form-text">
                Maximum file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP
              </div>
            </div>

            {/* Phone Number */}
            <div className="mb-3">
              <label className="form-label">Phone Number</label>
              <input
                className="form-control"
                name="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="123-456-7890"
              />
            </div>

            {/* Address Line 1 */}
            <div className="mb-3">
              <label className="form-label">Address Line 1</label>
              <input
                className="form-control"
                name="address_line_1"
                value={formData.address_line_1}
                onChange={handleChange}
              />
            </div>

            {/* Address Line 2 */}
            <div className="mb-3">
              <label className="form-label">Address Line 2</label>
              <input
                className="form-control"
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleChange}
              />
            </div>

            {/* City, State, Postal Code - Responsive Row */}
            <div className="row g-3 mb-3">
              <div className="col-md-5">
                <label className="form-label">City</label>
                <input
                  className="form-control"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">State</label>
                <select
                  className="form-select"
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleChange}
                >
                  <option value="">-- Select State --</option>
                  {states.map((s) => (
                    <option key={s.state_id} value={s.state_id}>
                      {s.state_code} - {s.state_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Postal Code</label>
                <input
                  className="form-control"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  pattern="[0-9]{5}"
                  maxLength="5"
                />
              </div>
            </div>

            {/* ============================================ */}
            {/* INVOICE SETTINGS SECTION */}
            {/* ============================================ */}
            <div className="border-top pt-4 mt-4">
              <h5 className="mb-3">
                <i className="bi bi-receipt me-2"></i>
                Invoice Settings
              </h5>

              {/* Enable Invoices Checkbox */}
              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="enable_invoices"
                    name="enable_invoices"
                    checked={formData.enable_invoices}
                    onChange={handleChange}
                    role="switch"
                  />
                  <label className="form-check-label" htmlFor="enable_invoices">
                    <strong>Enable Invoice Generation</strong>
                  </label>
                </div>
                <div className="form-text">
                  When enabled, generates formal invoice summary PDFs in
                  addition to supporting documentation. Includes invoice
                  numbering, tax calculation inputs, and payment tracking.
                </div>
              </div>

              {/* Invoice Settings (shown when enabled) */}
              {formData.enable_invoices && (
                <div className="bg-light p-3 rounded mb-3">
                  <div className="row g-3">
                    {/* Invoice Number Prefix */}
                    <div className="col-md-4">
                      <label className="form-label">
                        Invoice Number Prefix
                      </label>
                      <input
                        className="form-control"
                        name="invoice_number_prefix"
                        value={formData.invoice_number_prefix}
                        onChange={handleChange}
                        placeholder="INV"
                        maxLength="10"
                      />
                      <div className="form-text">
                        Preview: <code>{getInvoiceNumberPreview()}</code>
                      </div>
                    </div>

                    {/* Invoice Notes */}
                    <div className="col-12">
                      <label className="form-label">Invoice Notes</label>
                      <textarea
                        className="form-control"
                        name="invoice_notes"
                        value={formData.invoice_notes}
                        onChange={handleChange}
                        rows="2"
                        placeholder="Thank you for your business!"
                        maxLength="500"
                      />
                      <div className="form-text">
                        This message appears at the bottom of each invoice PDF
                        (max 500 characters)
                      </div>
                    </div>
                  </div>

                  {/* Invoice Feature Info */}
                  <div className="alert alert-info mt-3 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Invoice Features Include:</strong>
                    <ul className="mb-0 mt-2">
                      <li>
                        Sequential invoice numbering (
                        {formData.invoice_number_prefix || "INV"}-YYYYMM-###)
                      </li>
                      <li>
                        Formal invoice summary PDF (separate from supporting
                        documentation)
                      </li>
                      <li>
                        Tax input during invoice generation (service tax &amp;
                        mileage tax)
                      </li>
                      <li>Payment tracking with partial payment support</li>
                      <li>Invoice aging reports</li>
                      <li>
                        Status workflow: Generated → Sent → Partially Paid →
                        Paid
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="d-grid gap-2 d-md-block mt-4">
              <button className="btn btn-primary" type="submit">
                <i className="bi bi-save me-2"></i>
                Save Tenant Information
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TenantInfoForm;
